import { createAsyncThunk } from '@reduxjs/toolkit';

import jwtAxios from '@nxsys/services/axios';
import {
  ExportAllFPSErrorParams,
  GetFPSListParams,
  GetEPSListParams,
  ValidateXMLParams,
  GetEPSListResponse,
  ResubmitEPSParamsType,
  GetCIS300ListParams,
  GetCIS300ListResponse,
  ResubmitCIS300ParamsType,
  NormalFPSResponseType,
  LeaverFPSResponseType,
  SubmitFPSParamsType,
  GetNormalFPSListParams,
  GetLeaveFPSListParams,
  SubmitLeaverFPSParamsType,
  GetPreSubmitEPSResponse,
  GetPreSubmitEPSParams,
  SaveCISDeductionsSufferedParamsType,
  SaveCISDeductionsSufferedResponseType,
  SubmitEPSParamsType,
} from '../types';
import { endPoints } from '@nxsys/services/endpoints';
import { FpsPayrollType } from '@nxsys/types/models/RTI/fps';
import { EPSPayrollType, EPSRecordType } from '@nxsys/types/models/RTI/eps';
import {
  Cis300PayrollType,
  Cis300RecordType,
} from '@nxsys/types/models/RTI/cis300';

export const getFPSRecords = createAsyncThunk(
  'rti/getFPSRecords',
  async (params: GetFPSListParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.rti.fps, { params });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteFPSRecord = createAsyncThunk(
  'rti/deleteFPSRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.rti.submitRequestFPS}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getFPSById = createAsyncThunk(
  'rti/getFPSById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.rti.fps}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const downloadFPSRecord = createAsyncThunk(
  'rti/downloadFPSRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`/rti/fps/${id}/download`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const downloadFPSError = createAsyncThunk(
  'rti/downloadFPSError',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`/rti/fps/${id}/error`, {
        responseType: 'blob',
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitFPSRecord = createAsyncThunk(
  'rti/resubmitFPSRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(`/rti/fps/${id}/resubmit`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const exportAllFPSError = createAsyncThunk(
  'rti/exportAllFPSError',
  async (params: ExportAllFPSErrorParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(endPoints.rti.exportAllFPSError, {
        params: {
          ...params,
          status: 'FAILED',
        },
      });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getListPayrollByFPSId = createAsyncThunk(
  'rti/getListPayrollByFPSId',
  async (id: string, { rejectWithValue }): Promise<FpsPayrollType[]> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.rti.fps}/${id}/payrolls`,
      );
      return response.data?.data || [];
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const validateXML = createAsyncThunk(
  'rti/validateXML',
  async (params: ValidateXMLParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.rti.validateXML, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

//EPS

export const getEPSRecords = createAsyncThunk(
  'rti/getEPSRecords',
  async (
    params: GetEPSListParams,
    { rejectWithValue },
  ): Promise<GetEPSListResponse & { queryParams: GetEPSListParams | null }> => {
    try {
      const response = await jwtAxios.get(endPoints.rti.eps, { params });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitEPS = createAsyncThunk(
  'rti/resubmitEPS',
  async (params: ResubmitEPSParamsType, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.rti.resubmitEPS, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getListPayrollByEPSId = createAsyncThunk(
  'rti/getListPayrollByEPSId',
  async (id: string, { rejectWithValue }): Promise<EPSPayrollType[]> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.rti.eps}/${id}/payrolls`,
      );
      return response.data?.data || [];
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getEPSById = createAsyncThunk(
  'rti/getEPSById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(`${endPoints.rti.eps}/${id}`);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const exportAllEPSErrors = createAsyncThunk(
  'rti/exportAllEPSErrors',
  async (
    params: GetEPSListParams,
    { rejectWithValue },
  ): Promise<EPSRecordType[]> => {
    try {
      const response = await jwtAxios.get(endPoints.rti.exportAllEPSError, {
        params: {
          ...params,
          status: 'FAILED',
        },
      });
      return response.data.data;
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCIS300Records = createAsyncThunk(
  'rti/getCIS300Records',
  async (
    params: GetCIS300ListParams,
    { rejectWithValue },
  ): Promise<GetCIS300ListResponse & { queryParams: GetCIS300ListParams | null }> => {
    try {
      const response = await jwtAxios.get(endPoints.rti.cis300, { params });
      return {
        data: response.data.data,
        pagination: {
          current: params.page || 0,
          pageSize: params.size || 20,
          totalDocs: response.data.totalDocs,
        },
        queryParams: params,
      };
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCIS300PayrollList = createAsyncThunk(
  'rti/getCIS300PayrollList',
  async (id: string, { rejectWithValue }): Promise<Cis300PayrollType[]> => {
    try {
      const response = await jwtAxios.get(
        `${endPoints.rti.cis300}/${id}/payrolls`,
      );
      return response.data?.data || [];
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteCIS300Record = createAsyncThunk(
  'rti/deleteCIS300Record',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.rti.deleteCIS300}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const resubmitCIS300 = createAsyncThunk(
  'rti/resubmitCIS300',
  async (params: ResubmitCIS300ParamsType, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.rti.resubmitCIS300,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getCIS300ById = createAsyncThunk(
  'rti/getCIS300ById',
  async (id: string, { rejectWithValue }): Promise<Cis300RecordType> => {
    try {
      const response = await jwtAxios.get(`${endPoints.rti.cis300}/${id}`);
      return response.data.data;
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getAllCIS300Errors = createAsyncThunk(
  'rti/getAllCIS300Errors',
  async (
    params: GetCIS300ListParams,
    { rejectWithValue },
  ): Promise<Cis300RecordType[]> => {
    try {
      const response = await jwtAxios.get(endPoints.rti.exportAllCIS300Error, {
        params: {
          ...params,
          status: 'FAILED',
        },
      });
      return response.data.data;
    } catch (error: any) {
      throw rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getNormalFPS = createAsyncThunk<
  NormalFPSResponseType,
  GetNormalFPSListParams
>('rti/getNormalFPS', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.dashboard.payment, {
      params: {
        ...params,
        type: 'FPS',
      },
    });
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
    };
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const getLeaveFPS = createAsyncThunk<
  LeaverFPSResponseType,
  GetLeaveFPSListParams
>('rti/getLeaveFPS', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.rti.leaverFPS, { params });
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
    };
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const submitFPS = createAsyncThunk(
  'rti/submitFPS',
  async (params: SubmitFPSParamsType, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.rti.submitRequestFPS,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const submitLeaverFPS = createAsyncThunk(
  'rti/submitLeaverFPS',
  async (params: SubmitLeaverFPSParamsType, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(endPoints.rti.leaverFPS, params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getPreSubmitEPS = createAsyncThunk<
  GetPreSubmitEPSResponse,
  GetPreSubmitEPSParams
>('rti/getPreSubmitEPS', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.get(endPoints.rti.manualSubmitEPS, {
      params,
    });
    return {
      data: response.data.data,
      pagination: {
        current: params.page || 0,
        pageSize: params.size || 20,
        totalDocs: response.data.totalDocs,
      },
    };
  } catch (error: any) {
    throw rejectWithValue(error.response?.data || error.message);
  }
});

export const epsSaveCisDeductionsSuffered = createAsyncThunk<
  SaveCISDeductionsSufferedResponseType,
  SaveCISDeductionsSufferedParamsType
>('rti/epsSaveCisDeductionsSuffered', async (params, { rejectWithValue }) => {
  try {
    const response = await jwtAxios.post(
      `${endPoints.company.index}/${params?.companyId}/cis-deductions-suffered`,
      params,
      { params },
    );
    return response.data;
  } catch (e) {
    return rejectWithValue(e);
  }
});

export const submitEPS = createAsyncThunk(
  'rti/submitEPS',
  async (params: SubmitEPSParamsType, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.rti.submitRequestEPS,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteEPSRecord = createAsyncThunk(
  'rti/deleteEPSRecord',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.delete(
        `${endPoints.rti.submitRequestEPS}/${id}`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export type SendSafeRecRTIType = 'FPS' | 'EPS' | 'CIS300';

export type SendSafeRecRTIParams = {
  type: SendSafeRecRTIType;
  submissionId: string | number;
};

export const sendSafeRecRTI = createAsyncThunk(
  'rti/sendSafeRecRTI',
  async (params: SendSafeRecRTIParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.post(
        endPoints.payroll.safeRecRTI,
        params,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export type GetSafeRecRTIHistoryLatestParams = {
  // PAYSLIP: submissionId is the payrollId.
  type: SendSafeRecRTIType | 'PAYSLIP';
  submissionId: string | number;
};

export const getSafeRecRTIHistoryLatest = createAsyncThunk(
  'rti/getSafeRecRTIHistoryLatest',
  async (params: GetSafeRecRTIHistoryLatestParams, { rejectWithValue }) => {
    try {
      const response = await jwtAxios.get(
        endPoints.payroll.safeRecRTIHistoryLatest,
        { params },
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const rtiAct = {
  getFPSRecords,
  getFPSById,
  deleteFPSRecord,
  downloadFPSRecord,
  downloadFPSError,
  resubmitFPSRecord,
  exportAllFPSError,
  getListPayrollByFPSId,
  validateXML,
  getEPSRecords,
  resubmitEPS,
  getListPayrollByEPSId,
  getEPSById,
  exportAllEPSErrors,
  getCIS300Records,
  getCIS300PayrollList,
  deleteCIS300Record,
  resubmitCIS300,
  getCIS300ById,
  getAllCIS300Errors,
  getNormalFPS,
  getLeaveFPS,
  submitFPS,
  submitLeaverFPS,
  getPreSubmitEPS,
  epsSaveCisDeductionsSuffered,
  submitEPS,
  deleteEPSRecord,
  sendSafeRecRTI,
  getSafeRecRTIHistoryLatest,
};
